import React, { useState, useMemo } from "react";
import type { Recipe } from "../../data/recipes/types";
import type { AdminData } from "./AdminLayout";

interface Props {
  data: AdminData;
  updateData: (updater: (prev: AdminData) => AdminData) => void;
}

interface RecipeForm {
  id: string;
  name: string;
  nameFr: string;
  description: string;
  descriptionFr: string;
  vegetarian: boolean;
  vegan: boolean;
  glutenFree: boolean;
  spicy: boolean;
}

const emptyForm: RecipeForm = {
  id: "",
  name: "",
  nameFr: "",
  description: "",
  descriptionFr: "",
  vegetarian: false,
  vegan: false,
  glutenFree: false,
  spicy: false,
};

const RecipesManager: React.FC<Props> = ({ data, updateData }) => {
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState<"add" | "edit" | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<RecipeForm>(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const recipeEntries = useMemo(() => {
    return Object.entries(data.recipes)
      .filter(([id, r]) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          r.name.toLowerCase().includes(q) ||
          r.nameFr.toLowerCase().includes(q) ||
          id.includes(q)
        );
      })
      .sort(([, a], [, b]) => a.name.localeCompare(b.name));
  }, [data.recipes, search]);

  const getReferencingItems = (recipeId: string) => {
    return data.menuItems.filter((item) => item.recipeId === recipeId);
  };

  const openAdd = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
    setModal("add");
  };

  const openEdit = (id: string, recipe: Recipe) => {
    setForm({
      id,
      name: recipe.name,
      nameFr: recipe.nameFr,
      description: recipe.description,
      descriptionFr: recipe.descriptionFr,
      vegetarian: recipe.vegetarian || false,
      vegan: recipe.vegan || false,
      glutenFree: recipe.glutenFree || false,
      spicy: recipe.spicy || false,
    });
    setEditingId(id);
    setModal("edit");
  };

  const handleSave = () => {
    if (!form.id || !form.name || !form.nameFr) return;

    const camelId = form.id.replace(/-([a-z])/g, (_, c: string) =>
      c.toUpperCase()
    );

    const recipe: Recipe = {
      id: form.id,
      name: form.name,
      nameFr: form.nameFr,
      nameKey: `recipes.${camelId}.name`,
      description: form.description,
      descriptionFr: form.descriptionFr,
      descriptionKey: `recipes.${camelId}.description`,
    };

    if (form.vegetarian) recipe.vegetarian = true;
    if (form.vegan) recipe.vegan = true;
    if (form.glutenFree) recipe.glutenFree = true;
    if (form.spicy) recipe.spicy = true;

    updateData((prev) => {
      const recipes = { ...prev.recipes };

      if (modal === "add" && form.id in recipes) {
        alert("A recipe with this ID already exists.");
        return prev;
      }

      // If editing and ID changed, remove old entry
      if (modal === "edit" && editingId && editingId !== form.id) {
        delete recipes[editingId];
      }

      recipes[form.id] = recipe;
      return { ...prev, recipes };
    });

    setModal(null);
  };

  const handleDelete = (id: string) => {
    const refs = getReferencingItems(id);
    if (refs.length > 0) {
      alert(
        `Cannot delete: ${refs.length} menu item(s) reference this recipe.\n\nRemove the menu items first.`
      );
      setDeleteConfirm(null);
      return;
    }

    updateData((prev) => {
      const recipes = { ...prev.recipes };
      delete recipes[id];
      return { ...prev, recipes };
    });
    setDeleteConfirm(null);
  };

  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2>Recipes ({Object.keys(data.recipes).length})</h2>
        <button className="btn btn-primary" onClick={openAdd}>
          + Add Recipe
        </button>
      </div>

      <div className="admin-toolbar">
        <input
          type="text"
          placeholder="Search recipes..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="admin-item-count">
          {recipeEntries.length} recipes shown
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Name (FR)</th>
              <th>Description</th>
              <th>Flags</th>
              <th>Used By</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {recipeEntries.map(([id, recipe]) => {
              const refs = getReferencingItems(id);
              return (
                <tr key={id}>
                  <td>
                    <strong>{recipe.name}</strong>
                    <br />
                    <span style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                      {id}
                    </span>
                  </td>
                  <td>{recipe.nameFr}</td>
                  <td
                    style={{
                      maxWidth: "250px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {recipe.description}
                  </td>
                  <td>
                    {recipe.vegetarian && (
                      <span className="admin-badge vegetarian">veg</span>
                    )}
                    {recipe.vegan && (
                      <span className="admin-badge vegan">vegan</span>
                    )}
                    {recipe.glutenFree && (
                      <span className="admin-badge gf">GF</span>
                    )}
                    {recipe.spicy && (
                      <span className="admin-badge spicy">spicy</span>
                    )}
                  </td>
                  <td>{refs.length} items</td>
                  <td className="admin-table-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => openEdit(id, recipe)}
                    >
                      Edit
                    </button>
                    {deleteConfirm === id ? (
                      <>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(id)}
                        >
                          Confirm
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteConfirm(id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div className="admin-modal-overlay" onClick={() => setModal(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{modal === "add" ? "Add Recipe" : "Edit Recipe"}</h3>

            <div className="admin-field">
              <label>Recipe ID (kebab-case)</label>
              <input
                value={form.id}
                onChange={(e) => setForm({ ...form, id: e.target.value })}
                disabled={modal === "edit"}
                placeholder="e.g. grilled-chicken-breast"
              />
            </div>

            <div className="admin-field-row">
              <div className="admin-field">
                <label>Name (EN)</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>
              <div className="admin-field">
                <label>Name (FR)</label>
                <input
                  value={form.nameFr}
                  onChange={(e) => setForm({ ...form, nameFr: e.target.value })}
                />
              </div>
            </div>

            <div className="admin-field-row">
              <div className="admin-field">
                <label>Description (EN)</label>
                <textarea
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                />
              </div>
              <div className="admin-field">
                <label>Description (FR)</label>
                <textarea
                  value={form.descriptionFr}
                  onChange={(e) =>
                    setForm({ ...form, descriptionFr: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="admin-field">
              <label>Dietary Flags</label>
              <div className="admin-checkbox-group">
                {(
                  [
                    ["vegetarian", "Vegetarian"],
                    ["vegan", "Vegan"],
                    ["glutenFree", "Gluten Free"],
                    ["spicy", "Spicy"],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={form[key]}
                      onChange={(e) =>
                        setForm({ ...form, [key]: e.target.checked })
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="admin-modal-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setModal(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                {modal === "add" ? "Add Recipe" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipesManager;
